import {
  Box,
  Button,
  FormControl,
  FormLabel,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import Delayed from '~/src/components/Delayed';
import LoaderOverlay from '~/src/components/LoaderOverlay';
import useClearEffect from '../hooks/useClearEffect';
import useForgotPassword from '../hooks/useForgotPassword';
import {
  canSetNewPasswordSelector,
  confirmPasswordState,
  emailState,
  emailValidSelector,
  errorKeySelector,
  isLoadingState,
  passwordsMatchSelector,
  passwordState,
  passwordValidSelector,
  resetPasswordActiveState,
} from '../store';
import Layout from './NewLayout';

const useStyles = makeStyles(({ palette }) => ({
  container: {
    overflow: 'hidden',
    '& .forgotPasswordTitle': {
      fontWeight: 600,
      fontSize: '22px',
      lineHeight: '1.6',
      margin: '0 0 12px 0',
    },
    '& .forgotPasswordButton': {
      background: '#000000',
      color: '#FFFFFF',
      borderColor: '#000000',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '.5rem',
      lineHeight: '1.6',
      padding: '12px 32px',
      borderRadius: '25px',
      fontWeight: 600,
      fontSize: '16px',
      border: '2px solid transparent',
      '&.Mui-disabled': {
        color: '#a6a6a6',
        borderColor: 'rgba(0, 0, 0, 0.12)',
        backgroundColor: 'rgba(0, 0, 0, 0.12)',
      },
    },
  },
  formControlContainer: {
    '& label': {
      padding: 0,
      fontWeight: 600,
      lineHeight: '1.6',
      fontSize: '13px',
      marginBottom: '6px',
      color: 'var(--black)',
    },
    '& .MuiFilledInput-root': {
      background: 'none',
      borderRadius: '0',
    },
    '& input': {
      width: '100%',
      border: '1.5px solid #aba69c',
      borderRadius: '10px !important',
      fontSize: '15px',
    },
  },
  signLink: {
    color: palette.secondary.main,
    fontWeight: 600,
    lineHeight: 1.5,
    marginLeft: '5px',
  },
}));

const ForgotPassword = () => {
  const { container, formControlContainer } = useStyles();
  const { t } = useTranslation('auth');
  const [resetPassword, completePasswordReset] = useForgotPassword();
  const [user, setUser] = useRecoilState(emailState);
  const [pass, setPass] = useRecoilState(passwordState);
  const [confirmPass, setConfirmPass] = useRecoilState(confirmPasswordState);
  const userValid = useRecoilValue(emailValidSelector);
  const isPasswordValid = useRecoilValue(passwordValidSelector);
  const doPasswordsMatch = useRecoilValue(passwordsMatchSelector);
  const loginPending = useRecoilValue(isLoadingState);
  const errorKey = useRecoilValue(errorKeySelector);
  const resetPasswordActive = useRecoilValue(resetPasswordActiveState);
  const canSetNewPassword = useRecoilValue(canSetNewPasswordSelector);
  const [userTouched, setUserTouched] = useState(false);
  const [confirmationCodeTouched, setConfirmationCodeTouched] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [passTouched, setPassTouched] = useState(false);
  const [confirmPassTouched, setConfirmPassTouched] = useState(false);

  useClearEffect();

  const handleUserChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserTouched(true);
    setUser(e.target.value);
  };

  const handleConfirmCodeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationCodeTouched(true);
    setConfirmationCode(e.target.value);
  };

  const handlePassChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassTouched(true);
    setPass(e.target.value);
  };

  const handleConfirmPassChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassTouched(true);
    setConfirmPass(e.target.value);
  };

  const handleEnterKeyPressOnResetPassword = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      if (!resetPasswordActive && userValid) {
        resetPassword();
      } else if (resetPasswordActive && canSetNewPassword) {
        completePasswordReset();
      }
    }
  };

  return (
    <Layout
      chipText={t('forgotPassword.layoutContainer.title')}
      description={t('forgotPassword.layoutContainer.description')}
      cursorAfterText={t('forgotPassword.layoutContainer.cursorAfterText')}
      cursorBeforeText={t('forgotPassword.layoutContainer.cursorBeforeText')}
      buttonArray={[
        { label: t('signUp.layoutContainer.buttonText'), link: '/login' },
        { label: t('signIn.layoutContainer.buttonText'), link: '/signup' },
      ]}
    >
      <Box className={container}>
        {loginPending && (
          <Delayed>
            <LoaderOverlay />
          </Delayed>
        )}

        <Box flex="auto">
          <Typography variant="h3" className="forgotPasswordTitle">
            {t('forgotPassword.title')}
          </Typography>

          {!!errorKey && (
            <>
              <Box mb={3}>
                <Alert severity="error">{t(errorKey)}</Alert>
              </Box>
            </>
          )}
          {!resetPasswordActive && (
            <Box mb={3}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('forgotPassword.email.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="email"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={user}
                  onChange={handleUserChanged}
                  onKeyPress={handleEnterKeyPressOnResetPassword}
                  error={
                    (userTouched && !user)
                    || (userTouched && user && !userValid)
                  }
                  helperText={
                    (userTouched && !user && t('signUp.email.required'))
                    || (userTouched
                      && user
                      && !userValid
                      && t('signUp.email.validation'))
                  }
                />
              </FormControl>
            </Box>
          )}
          {resetPasswordActive && (
            <>
              <Box mb={3}>
                <FormControl
                  className={formControlContainer}
                  fullWidth
                  required
                >
                  <FormLabel>{t('confirmSignUp.confirmCode.label')}</FormLabel>
                  <TextField
                    autoComplete="off"
                    type="text"
                    variant="filled"
                    hiddenLabel
                    InputProps={{ disableUnderline: true }}
                    FormHelperTextProps={{ variant: 'standard' }}
                    value={confirmationCode}
                    onChange={handleConfirmCodeChanged}
                    error={confirmationCodeTouched && !confirmationCode}
                    helperText={
                      confirmationCodeTouched
                      && !confirmationCode
                      && t('confirmSignUp.confirmCode.required')
                    }
                  />
                </FormControl>
              </Box>
              <Box mb={3}>
                <FormControl
                  className={formControlContainer}
                  fullWidth
                  required
                >
                  <FormLabel>{t('forgotPassword.password.label')}</FormLabel>
                  <TextField
                    autoComplete="off"
                    type="password"
                    variant="filled"
                    hiddenLabel
                    InputProps={{ disableUnderline: true }}
                    FormHelperTextProps={{ variant: 'standard' }}
                    value={pass}
                    onChange={handlePassChanged}
                    error={
                      (passTouched && !pass) || (!!pass && !isPasswordValid)
                    }
                    helperText={
                      (passTouched
                        && !pass
                        && t('forgotPassword.password.required'))
                      || (passTouched
                        && pass
                        && !isPasswordValid
                        && t('signUp.password.validation'))
                    }
                  />
                </FormControl>
              </Box>
              <Box mb={3}>
                <FormControl
                  className={formControlContainer}
                  fullWidth
                  required
                >
                  <FormLabel>
                    {t('forgotPassword.confirmPassword.label')}
                  </FormLabel>
                  <TextField
                    autoComplete="off"
                    type="password"
                    variant="filled"
                    hiddenLabel
                    InputProps={{ disableUnderline: true }}
                    FormHelperTextProps={{ variant: 'standard' }}
                    value={confirmPass}
                    onChange={handleConfirmPassChanged}
                    onKeyPress={handleEnterKeyPressOnResetPassword}
                    error={
                      (confirmPassTouched && !pass)
                      || (confirmPassTouched && !!pass && !doPasswordsMatch)
                    }
                    helperText={
                      (confirmPassTouched
                        && !confirmPass
                        && t('forgotPassword.confirmPassword.required'))
                      || (confirmPassTouched
                        && confirmPass
                        && !doPasswordsMatch
                        && t('signUp.confirmPassword.validation'))
                    }
                  />
                </FormControl>
              </Box>
            </>
          )}

          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            className="forgotPasswordButton"
            disabled={resetPasswordActive ? !canSetNewPassword : !userValid}
            onClick={
              resetPasswordActive
                ? () => completePasswordReset(confirmationCode)
                : () => resetPassword()
            }
          >
            {resetPasswordActive
              ? t('forgotPassword.buttons.newPassword')
              : t('forgotPassword.buttons.reset')}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default React.memo(ForgotPassword);
